import { toast } from "vue-sonner";
import router from "../router/index.ts";
import { useRedistributionQueueStore } from "../stores/redistributionQueueStore.ts";
import {
  fetchRedistributionResult,
  fetchRedistributionStatus,
} from "./apiClient.ts";
import {
  applyCompletedRedistributionResult,
  hydrateTraceLogFromResult,
} from "./applyRedistributionResult.ts";

const POLL_INTERVAL_MS = 5000;

let pollTimer: ReturnType<typeof setInterval> | undefined;

/**
 * Polls the status of every in-flight redistribution job on a fixed interval.
 *
 * This is the sole mechanism for detecting job completion (an SSE-based push notification was
 * tried previously and removed: Azure Front Door enforces a hard ~30 second timeout on every HTTP
 * connection, so any long-lived stream is forcibly disconnected on a predictable schedule well
 * before most jobs finish, and completion events published during the reconnect gap would be
 * permanently lost). Polling guarantees the frontend eventually learns the outcome of every job,
 * since each poll is its own short-lived request unaffected by that cap.
 */
export function startRedistributionPolling(): void {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    void pollRunningJobs();
  }, POLL_INTERVAL_MS);
}

async function pollRunningJobs(): Promise<void> {
  const store = useRedistributionQueueStore();
  const runningRequestIds = store.items
    .filter((item) => item.status === "running")
    .map((item) => item.requestId);

  await Promise.all(runningRequestIds.map((requestId) => pollOne(requestId)));
}

async function pollOne(requestId: string): Promise<void> {
  const store = useRedistributionQueueStore();
  try {
    const status = await fetchRedistributionStatus(requestId);
    if (status === null) {
      store.markFailed(requestId, "Request not found or result expired.");
      toast.error("Redistribution failed", {
        description: "Request not found or result expired.",
      });
      return;
    }

    switch (status.status) {
      case "Completed": {
        store.markCompleted(requestId);

        let result;
        try {
          result = await fetchRedistributionResult(requestId);
          applyCompletedRedistributionResult(result, requestId);
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : "Could not load redistribution results.";
          toast.error("Result display failed", {
            description: message,
          });
          return;
        }

        // Trace log is optional — never block navigation to Results.
        try {
          await hydrateTraceLogFromResult(result);
        } catch {
          // ignore
        }

        await router.push({ name: "results" });

        toast.success("Redistribution completed", {
          description: `Request from ${formatTime(status.submittedAt)} finished successfully.`,
        });
        break;
      }
      case "Failed": {
        const errorDescription =
          status.errorMessage && status.errorMessage.trim().length > 0
            ? status.errorMessage
            : "An unexpected error occurred.";

        store.markFailed(requestId, status.errorMessage);
        toast.error("Redistribution failed", {
          description: errorDescription,
        });
        break;
      }
      case "Cancelled": {
        store.markCancelled(requestId);
        toast.info("Redistribution cancelled", {
          description: `Request from ${formatTime(status.submittedAt)} was cancelled.`,
        });
        break;
      }
      default: {
        // Still running — nothing to do until the next poll.
        break;
      }
    }
  } catch {
    // Transient network error; the next poll tick will retry.
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}
